import { Coins } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useCreditsWidgetState } from '../../../lib/use-credits-widget-state';
import { creditsWidgetCopyKeys, CreditsWidgetCtaButton } from './CreditsWidgetShared';

/**
 * The inner-screen top banner's LOW and OUT states (Figma 16346-95933 / -95088) — a pill that HUGS its
 * text (never ellipsised), centered above the page header by its slot in `AppFrame`.
 *
 * DESKTOP-WEB ONLY: `AppFrame` renders it solely on the wide layout (mobile-web has no banner — it
 * follows native, surfacing the drawer card + in-chat widget instead), so this stays a single hug-content
 * line and never needs a wrapping/narrow variant.
 *
 * Tokens are 1:1 with the frame: `Surface/Soft` fill + `Border/light/Default` hairline, radius 16,
 * px-20/py-12, gap-20 (group ↔ button), gap-12 (title ↔ body), gap-4 (icon ↔ title); title 16/600
 * (-0.04em), body 14/400 (-0.02em), both `Text & Icon/Primary`. The CTA is the shared accent button.
 * Returns null when the credit state is `none`.
 */
export function CreditsBanner() {
  const { t } = useTranslation();
  const state = useCreditsWidgetState();
  if (state.level === 'none') return null;
  const { titleKey, bodyKey, params } = creditsWidgetCopyKeys(state);

  return (
    <div
      role="status"
      style={{
        // Hug the content and stay centered by the slot (no max-width, no ellipsis). pointer-events
        // re-enabled here; the slot is pass-through.
        display: 'inline-flex',
        alignItems: 'center',
        gap: 20,
        padding: '12px 20px',
        borderRadius: 16,
        background: 'var(--ds-surface-light-gray)', // Surface/Soft #eae9e9 (dark-safe)
        border: '1px solid var(--ds-separator)', // Border/light/Default #dbdbdb (dark-safe)
        color: 'var(--ds-text-primary)',
        pointerEvents: 'auto',
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Coins size={16} strokeWidth={2} aria-hidden style={{ flexShrink: 0 }} />
          <span style={{ fontSize: 16, fontWeight: 'var(--sym-weight-semibold)', lineHeight: '20px', letterSpacing: '-0.04em', whiteSpace: 'nowrap' }}>
            {t(titleKey, params)}
          </span>
        </span>
        <span style={{ fontSize: 14, fontWeight: 400, lineHeight: '20px', letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>
          {t(bodyKey, params)}
        </span>
      </span>
      <span style={{ flexShrink: 0 }}>
        <CreditsWidgetCtaButton />
      </span>
    </div>
  );
}
